import { COMPOSITE_KEYS } from './constants';
import type { CacheOps } from './cache';
import type { QueueOps } from './queue';
import type { RouteMatcher } from './routes';
import type { OfflineStorage } from './storage';
import type { RouteMatch } from './types';
import { generateTempId, nowIso } from './timestamps';

export function createMutationHandlers(deps: {
  storage: OfflineStorage;
  cacheOps: CacheOps;
  queueOps: QueueOps;
  matchRoute: RouteMatcher['matchRoute'];
  assertCacheOwner?: () => Promise<void>;
}) {
  const { storage, cacheOps, queueOps, matchRoute, assertCacheOwner } = deps;
  const {
    cacheResponse,
    readCached,
    deleteEntity,
    getComposite,
    getEntity,
    setComposite,
    upsertEntity,
  } = cacheOps;

  async function handleOfflineMutation(
    method: string,
    url: string,
    payload: unknown,
    params?: Record<string, unknown>,
  ): Promise<unknown> {
    await assertCacheOwner?.();

    const route = matchRoute(method, url);

    if (!route || route.onlineOnly) {
      throw new Error('offline_not_supported');
    }

    const result = await applyOfflineMutation(route, method, url, payload, params);

    await queueOps.enqueue({
      method: method as 'POST' | 'PATCH' | 'DELETE',
      url,
      data: payload,
      entityType: route.entity,
      entityId: route.id ?? (result as Record<string, unknown> | undefined)?.id as string | undefined,
    });

    return result;
  }

  async function applyOfflineMutation(
    route: RouteMatch,
    method: string,
    url: string,
    payload: unknown,
    params?: Record<string, unknown>,
  ): Promise<unknown> {
    const ts = nowIso();
    const body = (payload ?? {}) as Record<string, unknown>;

    switch (route.entity) {
      case 'clients':
        if (route.operation === 'create') {
          const id = generateTempId();
          const client = {
            ...body,
            id,
            status: body.status ?? 'INACTIVE',
            coinsEnabled: body.coinsEnabled ?? false,
            coinBalance: body.coinBalance ?? 0,
            tags: body.tags ?? [],
            createdAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('clients', client, true);
          return strip(client);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('clients', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('clients', updated, true);
          return strip(updated);
        }
        break;

      case 'bookings':
        if (route.operation === 'create') {
          const id = generateTempId();
          const booking = {
            ...body,
            id,
            status: body.status ?? 'PENDING',
            createdAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('bookings', booking, true);
          return strip(booking);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('bookings', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('bookings', updated, true);
          return strip(updated);
        }
        if (route.operation === 'delete' && route.id) {
          await deleteEntity('bookings', route.id);
          return undefined;
        }
        break;

      case 'tasks':
        if (route.operation === 'create') {
          const id = generateTempId();
          const task = {
            ...body,
            id,
            status: body.status ?? 'OPEN',
            createdAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('tasks', task, true);
          return strip(task);
        }
        if (route.operation === 'action' && route.id) {
          const existing = await getEntity('tasks', route.id);
          if (!existing) throw new Error('offline_cache_miss');
          const updated = {
            ...existing,
            status: 'DONE',
            completedAt: ts,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('tasks', updated, true);
          return strip(updated);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('tasks', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('tasks', updated, true);
          return strip(updated);
        }
        if (route.operation === 'delete' && route.id) {
          await deleteEntity('tasks', route.id);
          return undefined;
        }
        break;

      case 'leads':
        if (route.operation === 'create') {
          const id = generateTempId();
          const lead = {
            ...body,
            id,
            stage: body.stage ?? 'INQUIRY',
            source: body.source ?? 'UNKNOWN',
            createdAt: ts,
            updatedAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('leads', lead, true);
          return strip(lead);
        }
        if (route.operation === 'action' && route.id) {
          const existing = await getEntity('leads', route.id);
          if (!existing) throw new Error('offline_cache_miss');
          const updated = {
            ...existing,
            stage: 'CONVERTED',
            updatedAt: ts,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('leads', updated, true);
          return strip(updated);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('leads', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            updatedAt: ts,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('leads', updated, true);
          return strip(updated);
        }
        if (route.operation === 'delete' && route.id) {
          await deleteEntity('leads', route.id);
          return undefined;
        }
        break;

      case 'measurements':
        if (route.operation === 'create' && route.clientId) {
          const id = generateTempId();
          const measurement = {
            ...body,
            id,
            clientId: route.clientId,
            date: body.date ?? ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('measurements', measurement, true);
          return strip(measurement);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('measurements', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('measurements', updated, true);
          return strip(updated);
        }
        if (route.operation === 'delete' && route.id) {
          await deleteEntity('measurements', route.id);
          return undefined;
        }
        break;

      case 'packages':
        if (route.operation === 'create' && route.clientId) {
          const id = generateTempId();
          const pkg = {
            ...body,
            id,
            clientId: route.clientId,
            usedSessions: 0,
            status: 'ACTIVE',
            createdAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('packages', pkg, true);
          return strip(pkg);
        }
        if (route.operation === 'action' && route.id && route.clientId) {
          const existing = await getEntity('packages', route.id);
          if (!existing) throw new Error('offline_cache_miss');
          const isRestore = url.endsWith('/restore-session');
          const usedSessions = Number(existing.usedSessions ?? 0);
          const total = Number(existing.totalSessions ?? 0);
          const used = isRestore
            ? Math.max(0, usedSessions - 1)
            : usedSessions + 1;
          const updated = {
            ...existing,
            usedSessions: used,
            status: used >= total ? 'DEPLETED' : 'ACTIVE',
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('packages', updated, true);
          return strip(updated);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('packages', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('packages', updated, true);
          return strip(updated);
        }
        if (route.operation === 'delete' && route.id) {
          await deleteEntity('packages', route.id);
          return undefined;
        }
        break;

      case 'challenges':
        if (route.operation === 'create') {
          const id = generateTempId();
          const challenge = {
            ...body,
            id,
            status: body.status ?? 'ACTIVE',
            createdAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('challenges', challenge, true);
          return strip(challenge);
        }
        if (route.operation === 'delete' && route.id) {
          const existing = await getEntity('challenges', route.id);
          if (!existing) throw new Error('offline_cache_miss');
          const updated = { ...existing, status: 'ARCHIVED', _localUpdatedAt: ts, _pendingSync: true };
          await upsertEntity('challenges', updated, true);
          return strip(updated);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('challenges', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('challenges', updated, true);
          return strip(updated);
        }
        break;

      case 'trainingPlans':
        if (route.operation === 'create') {
          const id = generateTempId();
          const plan = {
            ...body,
            id,
            createdByAi: body.createdByAi ?? false,
            createdAt: ts,
            _localUpdatedAt: ts,
          };
          await upsertEntity('trainingPlans', plan, true);
          if (body.exercises !== undefined) {
            await setComposite(
              COMPOSITE_KEYS.trainingPlanDetail(id),
              { ...plan, exercises: body.exercises },
              true,
            );
          }
          return strip(plan);
        }
        if (route.operation === 'update' && route.id) {
          const existing = await getEntity('trainingPlans', route.id);
          const updated = {
            ...(existing ?? { id: route.id }),
            ...body,
            id: route.id,
            _localUpdatedAt: ts,
            _pendingSync: true,
          };
          await upsertEntity('trainingPlans', updated, true);
          const existingDetail = (await getComposite(
            COMPOSITE_KEYS.trainingPlanDetail(route.id),
          )) as Record<string, unknown> | undefined;
          const exercises =
            body.exercises !== undefined
              ? body.exercises
              : Array.isArray(existingDetail?.exercises)
                ? existingDetail.exercises
                : [];
          await setComposite(
            COMPOSITE_KEYS.trainingPlanDetail(route.id),
            { ...strip(updated), exercises },
            true,
          );
          return { ...strip(updated), exercises };
        }
        if (route.operation === 'delete' && route.id) {
          await deleteEntity('trainingPlans', route.id);
          await storage.deleteCompositeEntry(COMPOSITE_KEYS.trainingPlanDetail(route.id));
          return undefined;
        }
        break;

      case 'trainerProfile': {
        const existing = await getComposite(COMPOSITE_KEYS.trainerProfile);
        const profile = {
          ...((existing as Record<string, unknown>) ?? {}),
          ...body,
          _localUpdatedAt: ts,
        };
        await setComposite(COMPOSITE_KEYS.trainerProfile, profile, true);
        return profile;
      }

      case 'clientCoins':
        if (route.clientId && route.operation === 'update') {
          const key = COMPOSITE_KEYS.clientCoins(route.clientId);
          const existing = (await getComposite(key)) as Record<string, unknown> | undefined;
          const coins = existing ?? { coinBalance: 0, coinsEnabled: false, history: [] };
          const updated = { ...coins, coinsEnabled: body.coinsEnabled, _localUpdatedAt: ts };
          await setComposite(key, updated, true);
          return { coinsEnabled: body.coinsEnabled };
        }
        if (route.clientId && route.operation === 'action') {
          const key = COMPOSITE_KEYS.clientCoins(route.clientId);
          const existing = ((await getComposite(key)) as Record<string, unknown> | undefined) ?? {
            coinBalance: 0,
            coinsEnabled: true,
            history: [],
          };
          const amount = Number(body.amount ?? 0);
          const history = Array.isArray(existing.history) ? [...existing.history] : [];
          history.unshift({
            id: generateTempId(),
            clientId: route.clientId,
            amount: -amount,
            type: 'REDEEM',
            note: body.note,
            createdAt: ts,
          });
          const updated = {
            ...existing,
            coinBalance: Number(existing.coinBalance ?? 0) - amount,
            history,
          };
          await setComposite(key, updated, true);
          return updated;
        }
        break;
    }

    void url;
    void params;
    void method;
    throw new Error('offline_not_supported');
  }

  function strip(record: Record<string, unknown>): Record<string, unknown> {
    const { _localUpdatedAt, _pendingSync, ...rest } = record;
    return rest;
  }

  async function handleOfflineGet(
    url: string,
    params?: Record<string, unknown>,
  ): Promise<unknown> {
    await assertCacheOwner?.();

    const route = matchRoute('GET', url);
    if (!route) throw new Error('offline_cache_miss');
    return readCached(route, url, params);
  }

  async function cacheOnlineResponse(
    method: string,
    url: string,
    data: unknown,
    params?: Record<string, unknown>,
    options?: { force?: boolean; requestData?: Record<string, unknown> },
  ): Promise<void> {
    const route = matchRoute(method, url);
    if (route && !route.onlineOnly) {
      await cacheResponse(route, data, url, params, options);
    }
  }

  return {
    handleOfflineMutation,
    handleOfflineGet,
    cacheOnlineResponse,
  };
}

export type MutationHandlers = ReturnType<typeof createMutationHandlers>;
